/**
 * Dice notation: `2d6+3`, `d20`, `1d8 + 1d6 - 1`, `4dF`-style fudge dice are not supported.
 * Used by the `dice` field type to show min / average / max next to the raw expression.
 */

export interface DiceTerm {
  count: number;
  sides: number;
  sign: 1 | -1;
}

export interface DiceStats {
  terms: DiceTerm[];
  modifier: number;
  min: number;
  max: number;
  avg: number;
}

const TERM = /([+-])?\s*(?:(\d*)\s*[dD]\s*(\d+)|(\d+))/y;

export function parseDice(input: string): DiceStats | null {
  const src = input.trim();
  if (!src) return null;
  const terms: DiceTerm[] = [];
  let modifier = 0;
  let pos = 0;
  let first = true;
  while (pos < src.length) {
    while (src[pos] === ' ') pos++;
    if (pos >= src.length) break;
    TERM.lastIndex = pos;
    const m = TERM.exec(src);
    if (!m || (!first && !m[1])) return null;
    const sign: 1 | -1 = m[1] === '-' ? -1 : 1;
    if (m[3] !== undefined) {
      const count = m[2] ? Number(m[2]) : 1;
      const sides = Number(m[3]);
      if (!Number.isFinite(count) || !Number.isFinite(sides) || count < 1 || count > 1000 || sides < 1 || sides > 100000) return null;
      terms.push({ count, sides, sign });
    } else {
      modifier += sign * Number(m[4]);
    }
    pos = TERM.lastIndex;
    first = false;
  }
  if (!terms.length && first) return null;
  let min = modifier;
  let max = modifier;
  let avg = modifier;
  for (const t of terms) {
    const lo = t.count;
    const hi = t.count * t.sides;
    const mean = (t.count * (t.sides + 1)) / 2;
    if (t.sign === 1) {
      min += lo;
      max += hi;
      avg += mean;
    } else {
      min -= hi;
      max -= lo;
      avg -= mean;
    }
  }
  return { terms, modifier, min, max, avg };
}

/** Roll the expression with the given random source (defaults to Math.random). */
export function rollDice(stats: DiceStats, random: () => number = Math.random): number {
  let total = stats.modifier;
  for (const t of stats.terms) {
    for (let i = 0; i < t.count; i++) total += t.sign * (1 + Math.floor(random() * t.sides));
  }
  return total;
}
